import { Grid, MenuItem, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'

import {
  LOCATION_NAME_MAX_LENGTH,
  type LocationFormValues,
  TIMEZONE_OPTIONS,
  US_STATE_OPTIONS,
} from '@/definitions/location'
import { validatePhoneNumber } from '@/utils/phoneUtils'
import { validatePostalCode } from '@/utils/postalUtils'

const requireValue = (value: string) => value.trim().length > 0 || 'Required.'

/** `validatePhoneNumber` returns '' when the number is usable, so no error is a pass. */
const requirePhoneNumber = (value: string) => {
  const error = validatePhoneNumber(value)
  return error === '' || error
}

/** `validatePostalCode` returns '' when the ZIP is usable, so no error is a pass. */
const requirePostalCode = (value: string) => {
  const error = validatePostalCode(value)
  return error === '' || error
}

const requireSelection = (value: string) => value !== '' || 'Required.'

/**
 * The inputs that describe one location, and the rules they are held to.
 *
 * Reads its form off `useFormContext` rather than taking a `control` prop, so it
 * fits any form whose values include `LocationFormValues` without being typed to
 * a particular one — the setup flow collects these alongside the organization's
 * own fields. Any surface that collects a location therefore enforces identical
 * rules by construction rather than by remembering to keep copies in step.
 *
 * Callers supply their own surrounding copy; this renders fields only.
 */
export const LocationFields = () => {
  const { control } = useFormContext<LocationFormValues>()

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Controller
          name='location_name'
          control={control}
          rules={{ validate: requireValue }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='Location Name'
              placeholder='Main Office'
              required
              fullWidth
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              slotProps={{ htmlInput: { maxLength: LOCATION_NAME_MAX_LENGTH } }}
            />
          )}
        />
      </Grid>
      <Grid size={8}>
        <Controller
          name='street'
          control={control}
          rules={{ validate: requireValue }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='Street'
              placeholder='123 Vision Way'
              required
              fullWidth
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <Controller
          name='suite'
          control={control}
          render={({ field }) => (
            <TextField {...field} label='Suite' placeholder='Suite 200' fullWidth />
          )}
        />
      </Grid>
      <Grid size={5}>
        <Controller
          name='city'
          control={control}
          rules={{ validate: requireValue }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='City'
              placeholder='Austin'
              required
              fullWidth
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid size={3}>
        <Controller
          name='state'
          control={control}
          rules={{ validate: requireSelection }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='State'
              select
              required
              fullWidth
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            >
              {US_STATE_OPTIONS.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      <Grid size={4}>
        <Controller
          name='postal_code'
          control={control}
          rules={{ validate: requirePostalCode }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='ZIP'
              placeholder='78701'
              required
              fullWidth
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid size={6}>
        <Controller
          name='primary_phone'
          control={control}
          rules={{ validate: requirePhoneNumber }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='Phone'
              placeholder='(555) 123-4567'
              required
              fullWidth
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid size={6}>
        <Controller
          name='timezone'
          control={control}
          rules={{ validate: requireSelection }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label='Time Zone'
              select
              required
              fullWidth
              error={fieldState.invalid}
              helperText={
                fieldState.error?.message ?? 'Drives appointment times and reminder sends.'
              }
            >
              {TIMEZONE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
    </Grid>
  )
}
